import { gql } from "lib/apolloClient";
import {
  studentFragments,
  organizationStudentFeedFragment,
  studentCourseUserMapFragment,
  yearGroupFragments,
} from "./StudentFragments";
import { curriculumProgramFragment } from "modules/CommonFragments";

export const getStudentIdsQuery = gql`
  query getStudentIds(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(filters: $filters) {
          edges {
            node {
              id
              isBlocked
              isArchived
            }
          }
        }
        __typename
      }
      __typename
    }
  }
`;

export const getStudentFeedQuery = gql`
  query getStudentFeed(
    $organizationId: ID!
    $after: String
    $filters: OrgConnectionFilterInput
    $postFilters: PostFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $familiesFilter: FamiliesFiltersInput
    $staffFilter: StaffStudentRelationFilter
    $withStaffRelation: Boolean! = false
    $familyMembersCountFilter: FamilyMembersFilterInput
    $familyStatus: TotalCountsByFamilyStatusFilters
    $userCourseMapFilters: UserCourseMapFilters
    $userYearGroupMapFilters: YearGroupFilters
    $courseGradeFilters: CourseGradeFilters
    $yearGroupGradeFilters: YearGroupGradeFilters
    $studentCurriculumProgramFilter: StudentCurriculumProgramFilter
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationStudentFeedEdgeItem
      }
    }
  }
  ${organizationStudentFeedFragment.organizationStudentFeedEdge}
`;

export const getStudentFeedQueryV2 = gql`
  query getStudentFeed(
    $organizationId: ID!
    $after: String
    $filters: OrgConnectionFilterInput
    $filtersWithoutSearch: OrgConnectionFilterInput
    $postFilters: PostFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $familiesFilter: FamiliesFiltersInput
    $staffFilter: StaffStudentRelationFilter
    $withStaffRelation: Boolean! = false
    $familyMembersCountFilter: FamilyMembersFilterInput
    $familyStatus: TotalCountsByFamilyStatusFilters
    $userYearGroupMapFilters: YearGroupFilters
    $yearGroupGradeFilters: YearGroupGradeFilters
    $studentCurriculumProgramFilter: StudentCurriculumProgramFilter
    $userArchivedFilters: UserArchivedFilters
    $academicYears: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationStudentFeedEdgeItem
      }
    }
  }
  ${organizationStudentFeedFragment.organizationStudentFeedEdgeV2}
`;

export const getStudentCourseMapQuery = gql`
  query getStudentCourseMap(
    $id: ID!
    $userCourseMapFilters: UserCourseMapFilters
    $courseGradeFilters: CourseGradeFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        courseMap(filters: $userCourseMapFilters) {
          id
          course {
            id
            title
            isArchived
            profileImageData {
              url
              icon
              color
              acronym
            }
            curriculumProgram {
              ...curriculumProgramBasicDetailsItem
            }
            genericTags {
              id
              label
              type
            }
            grades(filters: $courseGradeFilters) {
              id
              name
            }
            grade {
              id
              yearGroup {
                id
              }
            }
          }
          tags {
            id
            label
          }
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
`;

export const getCoursesOfStudentQuery = gql`
  query getCoursesOfStudent($ids: [ID!], $filters: CourseFilter) {
    platform {
      student(ids: $ids) {
        id
        courses(filters: $filters) {
          id
          title
          profileImage
          isArchived
          userMap(filters: { userTypes: student }) {
            ...userMapItem
          }
          grades {
            id
            name
          }
          genericTags {
            id
            label
            type
          }
        }
      }
    }
  }
  ${studentCourseUserMapFragment.userMap}
`;

export const getFamilyOfStudentQuery = gql`
  query getFamilyOfStudent($ids: [ID!]) {
    platform {
      student(ids: $ids) {
        ...studentItem
      }
    }
  }
  ${studentFragments.studentFamilyDetails}
`;

export const getOrganizationYearGroupsQuery = gql`
  query getOrganizationYearGroups(
    $id: ID!
    $filters: YearGroupFilter
    $yearGroupGradeFilters: YearGroupGradeFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          ...basicYearGroupDetails
        }
      }
    }
  }
  ${yearGroupFragments.basicYearGroupDetails}
`;

export const getStudentsWithoutYearGroupCountQuery = gql`
  query getStudentsWithoutYearGroupCount(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        students(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getStudentsWithoutYearGroupQuery = gql`
  query getStudentsWithoutYearGroup(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
    $first: Int
    $after: String
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        students(filters: $filters, first: $first, after: $after) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              firstName
              lastName
              email
              isArchived
              profileImage
              courseMap {
                course {
                  id
                  title
                  curriculumProgram {
                    id
                  }
                  grade {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentDetailsQuery = gql`
  query getStudentDetails(
    $userId: ID!
    $userArchivedFilters: UserArchivedFilters
    $studentCurriculumProgramFilter: StudentCurriculumProgramFilter
    $userYearGroupMapFilters: YearGroupFilters
    $yearGroupGradeFilters: YearGroupGradeFilters
    $staffFilter: StaffStudentRelationFilter
    $withStaffRelation: Boolean! = false
    $userCourseMapFilters: UserCourseMapFilters
    $courseGradeFilters: CourseGradeFilters
    $familiesFilter: FamiliesFiltersInput
  ) {
    node(id: $userId, type: STUDENT) {
      id
      ...studentItem
    }
  }
  ${studentFragments.studentItem}
`;

// $yearGroupGradeFilters: YearGroupGradeFilters
export const getOrganizationYearGroupsWithGradesQuery = gql`
  query getOrganizationYearGroups(
    $id: ID!
    $filters: YearGroupFilter
    $academicYearGradeMapFilters: AcademicYearGradeMapFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        yearGroups(filters: $filters) {
          ...basicYearGroupDetails
          academicYearGradeMap(filters: $academicYearGradeMapFilters) {
            academicYear {
              id
              startDate
              endDate
              isCurrentAcademicYear
            }
            grade {
              id
              name
              studentCount
            }
          }
        }
      }
    }
  }
  ${yearGroupFragments.portfolioDownloadYearGroupDetails}
`;

export const getStudentBulkPortfolioDownloadJobCountQuery = gql`
  query getStudentPortfolioDownloadJobCount(
    $id: ID!
    $filters: OrganizationIntegrationJobFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        jobFeed(filters: $filters) {
          totalCount
          edges {
            node {
              id
              type
              status
              relatedEntities {
                entityType
                entity {
                  id
                }
              }
            }
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const getTeacherFeedQuery = gql`
  query getTeacherFeed(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        staff(
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          userCount {
            role
            totalCount
          }
          edges {
            node {
              id
              ... on Staff {
                id
                firstName
                lastName
                isArchived
              }
            }
          }
        }
      }
    }
  }
`;

export const getStudentsTotalCountQuery = gql`
  query getStudentsTotalCount(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        students(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;
